import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import PublicLayout from "../../layouts/PublicLayout";
import PrivateLayout from "../../layouts/PrivateLayout";
import useLanguage from "../../utils/useLanguage";
import { redirectToBattleNetLogin } from "../../utils/auth";
import { useCart } from "../../context/CartContext";
import "@fortawesome/fontawesome-free/css/all.min.css";

const BGN_TO_EUR = 1.95583;
const toEur = (bgn) => (bgn / BGN_TO_EUR).toFixed(2);

const t = {
  en: {
    storeTitle: "Terra Guild Store",
    storeSubtitle: "Represent the guild. Official Terra merchandise — crafted for champions.",
    storeLaunching: "Store launching soon — orders open below",
    addToCart: "Add to Cart",
    added: "Added!",
    bgnLabel: "lv.",
    footer: "All products are guild-exclusive. Shipping worldwide. Questions?",
    footerContact: "Contact an officer in Discord.",
    badges: { Bestseller: "Bestseller", New: "New", Popular: "Popular" },
    products: [
      { name: "Terra Guild T-Shirt", description: "Classic black tee with the Terra guild emblem. Premium cotton blend." },
      { name: "Terra Mug", description: "Start every raid day right. 11oz ceramic mug with the Terra logo." },
      { name: "Terra Hoodie", description: "Stay warm in the cold raids ahead. Heavyweight pullover hoodie." },
      { name: "Terra Cap", description: "Structured snapback with embroidered Terra crest. One size fits all." },
      { name: "Terra Mouse Pad", description: "Large XL desk mat (90×40cm) with full-art Terra guild artwork." },
      { name: "Terra Sticker Pack", description: "10-piece holographic vinyl sticker set. Weather-resistant and waterproof." },
    ],
  },
  bg: {
    storeTitle: "Магазин на Гилдия Тера",
    storeSubtitle: "Представи гилдията. Официални стоки на Тера — създадени за шампиони.",
    storeLaunching: "Магазинът е отворен — поръчай сега",
    addToCart: "Добави в кошницата",
    added: "Добавено!",
    bgnLabel: "лв.",
    footer: "Всички продукти са ексклузивни за гилдията. Доставка в целия свят. Въпроси?",
    footerContact: "Свържете се с офицер в Discord.",
    badges: { Bestseller: "Бестселър", New: "Ново", Popular: "Популярно" },
    products: [
      { name: "Тениска на Гилдия Тера", description: "Класическа черна тениска с емблемата на гилдия Тера. Висококачествена памучна смес." },
      { name: "Чаша Тера", description: "Започни всеки ден за рейд правилно. Керамична чаша 325мл с логото на Тера." },
      { name: "Суитшърт Тера", description: "Остани топъл в студените рейдове напред. Тежък пуловер с качулка." },
      { name: "Шапка Тера", description: "Структурирана шапка с бродирания герб на Тера. Един размер за всички." },
      { name: "Подложка за Мишка Тера", description: "Голяма XL подложка (90×40см) с пълен арт на гилдия Тера." },
      { name: "Стикер Пакет Тера", description: "10 холографски винилови стикера. Устойчиви на влага и вода." },
    ],
  },
};

const productsMeta = [
  { id: 1, rawPrice: 29.99, price: "29.99 лв.", image: "/products/tshirt.svg",   badge: "Bestseller", badgeColor: "bg-yellow-500", logoSize: "h-9 w-9",  logoPos: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" },
  { id: 2, rawPrice: 14.99, price: "14.99 лв.", image: "/products/mug.svg",       badge: null,         logoSize: "h-11 w-11", logoPos: "top-[60%] left-1/2 -translate-x-1/2 -translate-y-1/2" },
  { id: 3, rawPrice: 54.99, price: "54.99 лв.", image: "/products/hoodie.svg",   badge: "New",        badgeColor: "bg-green-500", logoSize: "h-9 w-9",  logoPos: "top-[60%] left-1/2 -translate-x-1/2 -translate-y-1/2" },
  { id: 4, rawPrice: 24.99, price: "24.99 лв.", image: "/products/cap.svg",       badge: null,         logoSize: "h-9 w-9",  logoPos: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" },
  { id: 5, rawPrice: 19.99, price: "19.99 лв.", image: "/products/mousepad.svg", badge: null,         logoSize: "h-11 w-11", logoPos: "top-[60%] left-1/2 -translate-x-1/2 -translate-y-1/2" },
  { id: 6, rawPrice: 9.99,  price: "9.99 лв.",  image: "/products/stickers.svg", badge: "Popular",   badgeColor: "bg-orange-500", logoSize: null, logoPos: null },
];

const ProductCard = ({ meta, text, strings }) => {
  const { addToCart } = useCart();
  const [justAdded, setJustAdded] = useState(false);
  const badgeLabel = meta.badge ? strings.badges[meta.badge] : null;

  const handleAdd = () => {
    addToCart({ ...meta, name: text.name });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1500);
  };

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden shadow-xl hover:shadow-blue-900/30 hover:border-blue-700 transition-all duration-300 group flex flex-col">
      <div className="relative bg-white h-44 sm:h-52 flex items-center justify-center overflow-hidden">
        {badgeLabel && (
          <span className={`absolute top-3 right-3 ${meta.badgeColor} text-white text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide shadow z-10`}>
            {badgeLabel}
          </span>
        )}
        <img
          src={meta.image}
          alt={text.name}
          className="h-full w-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
        />
        {/* Terra logo on product */}
        {meta.logoSize && (
          <img
            src="/new-logo-terra.png"
            alt="Terra"
            className={`absolute ${meta.logoPos} ${meta.logoSize} drop-shadow-lg opacity-85 z-10 pointer-events-none`}
          />
        )}
      </div>

      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-white font-semibold text-base sm:text-lg leading-tight">
            {text.name}
          </h3>
          <div className="flex flex-col items-end ml-3 flex-shrink-0">
            <span className="text-blue-400 font-bold text-base sm:text-lg leading-tight">
              {meta.rawPrice.toFixed(2)} {strings.bgnLabel}
            </span>
            <span className="text-gray-400 text-xs font-medium">
              {toEur(meta.rawPrice)} €
            </span>
          </div>
        </div>
        <p className="text-gray-400 text-sm leading-relaxed flex-1 mb-4">
          {text.description}
        </p>
        <button
          onClick={handleAdd}
          className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
            justAdded
              ? "bg-green-600 text-white cursor-default scale-95"
              : "bg-blue-600 hover:bg-blue-500 text-white hover:shadow-lg hover:shadow-blue-900/40 active:scale-95"
          }`}
        >
          {justAdded ? (
            <span><i className="fas fa-check mr-2"></i>{strings.added}</span>
          ) : (
            <span><i className="fas fa-shopping-basket mr-2"></i>{strings.addToCart}</span>
          )}
        </button>
      </div>
    </div>
  );
};

function Shop() {
  const { lang } = useLanguage();
  const strings = t[lang];
  const isLoggedIn = !!Cookies.get("jwt");
  const navigate = useNavigate();
  const { totalItems } = useCart();

  const Wrapper = isLoggedIn ? PrivateLayout : PublicLayout;

  return (
    <Wrapper>
      <div className="min-h-screen flex flex-col">
        <div
          className="fixed inset-0 -z-10"
          style={{
            backgroundImage: "url('/wow-wallpaper.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="fixed inset-0 bg-black/70 -z-10 pointer-events-none" />

        <div className="relative z-10 flex flex-col min-h-screen">
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="max-w-6xl mx-auto">
            {/* Cart access */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => navigate("/cart")}
                className="relative flex items-center space-x-2 text-gray-300 hover:text-white border border-gray-700 hover:border-gray-500 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                title={lang === "bg" ? "Кошница" : "Cart"}
              >
                <i className="fas fa-shopping-basket"></i>
                <span>{lang === "bg" ? "Кошница" : "Cart"}</span>
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {totalItems > 9 ? "9+" : totalItems}
                  </span>
                )}
              </button>
            </div>

            {/* Hero */}
            <div className="text-center mb-10 sm:mb-14">
              <div className="flex justify-center mb-6">
                <Link
                  to="/raid-signups"
                  onClick={(e) => {
                    if (!isLoggedIn) {
                      e.preventDefault();
                      redirectToBattleNetLogin();
                    }
                  }}
                  className="relative inline-flex items-center justify-center group"
                >
                  <span className="absolute inset-0 rounded-xl bg-emerald-400 opacity-40 animate-ping-slow"></span>
                  <span className="relative inline-flex items-center gap-3 bg-emerald-500 group-hover:bg-emerald-400 text-white font-bold text-lg sm:text-xl px-8 py-4 rounded-xl shadow-lg shadow-emerald-900/40 group-hover:shadow-emerald-700/60 transition-all duration-300 group-hover:scale-105">
                    <i className="fas fa-shield-halved"></i>
                    ЗАПИШИ СЕ ЗА СЕЗОН 2
                  </span>
                </Link>
              </div>
              <div className="flex items-center justify-center mb-3">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-600 to-transparent max-w-xs"></div>
                <img src="/new-logo-terra.png" alt="Terra" className="h-10 w-10 mx-4 drop-shadow-lg" />
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-600 to-transparent max-w-xs"></div>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-serif tracking-wide mb-3">
                {strings.storeTitle}
              </h1>
              <p className="text-gray-300 text-base sm:text-lg max-w-xl mx-auto">
                {strings.storeSubtitle}
              </p>
              <div className="mt-4 inline-flex items-center space-x-2 bg-blue-900/40 border border-blue-700/50 text-blue-300 text-xs sm:text-sm px-4 py-2 rounded-full">
                <i className="fas fa-shopping-basket"></i>
                <span>{strings.storeLaunching}</span>
              </div>
            </div>

            {/* Product grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {productsMeta.map((meta, i) => (
                <ProductCard
                  key={meta.id}
                  meta={meta}
                  text={strings.products[i]}
                  strings={strings}
                />
              ))}
            </div>

            <p className="text-center text-gray-500 text-xs sm:text-sm mt-12">
              {strings.footer}{" "}
              <a
                href="https://discord.gg/xTMj79E9C"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline underline-offset-2"
              >
                {strings.footerContact}
              </a>
            </p>
          </div>
        </main>
        </div>
      </div>
    </Wrapper>
  );
}

export default Shop;
